/** @file Sets a mesh deform key from absolute vertices or relative offsets, stored trimmed. */
import { z } from 'zod';
import type { Curve, DeformKey, MeshAtt } from '@pose2d/format/types';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { animationDraft } from './animationDraft';
import { insertByFrame } from './insertByFrame';
import { meshOffsets } from './meshOffsets';
import { sameJson } from './sameJson';

type Offsets = { start: number; values: number[] };
type DeformInput = { offsets?: Offsets | null; vertices?: number[] };

/** True when `same` already stores `offsets` and the given curve (an absent curve keeps the stored one). */
function unchanged(same: DeformKey | undefined, offsets: Offsets | null, curve: Curve | undefined): boolean {
  return same !== undefined && sameJson(same.offsets, offsets) && (curve === undefined || sameJson(same.curve, curve));
}

/** VALIDATION at `offsets` unless start and length are even and the range fits `size` numbers. */
function checkRange(o: Offsets, size: number): void {
  const fits = o.start % 2 === 0 && o.values.length % 2 === 0 && o.start + o.values.length <= size;
  if (!fits) throwError('VALIDATION', undefined, { path: 'offsets', hint: `start and values.length must be even and start + values.length <= ${size} (2 per vertex)` });
}

/** The full offsets array (2 per vertex) the input describes, relative to the mesh's rest vertices. */
function fullOffsets(mesh: MeshAtt, input: DeformInput): number[] {
  const rest = meshOffsets.rest(mesh);
  if (input.vertices !== undefined) {
    if (input.vertices.length !== rest.length) throwError('VALIDATION', undefined, { path: 'vertices', hint: `expected ${rest.length} numbers (2 per vertex)` });
    return input.vertices.map((v, i) => v - (rest[i] ?? 0));
  }
  const offsets = input.offsets ?? null;
  if (offsets) checkRange(offsets, rest.length);
  return meshOffsets.expand(offsets, rest.length);
}

/** animation.setDeformKey: absolute `vertices` or relative `offsets` in, trimmed offsets (or null) stored; same key = no-op. */
export const command = defineCommand({
  name: 'animation.setDeformKey',
  description: 'Set the deform key of mesh `attachment` at `frame` from absolute `vertices` (rest space, 2 per vertex) or relative `offsets` { start, values } (null = rest shape); curve optional.',
  input: z
    .object({
      animation: z.string().min(1),
      attachment: z.string().min(1),
      frame: prim.frame,
      offsets: z.object({ start: z.number().int().nonnegative(), values: prim.numbers }).strict().nullable().exactOptional(),
      vertices: prim.numbers.exactOptional(),
      curve: prim.curve.exactOptional(),
    })
    .strict(),
  apply(draft, input, ctx) {
    if ((input.offsets === undefined) === (input.vertices === undefined)) {
      throwError('VALIDATION', undefined, { path: 'offsets', hint: 'pass exactly one of vertices (absolute) or offsets (relative)' });
    }
    const { anim } = animationDraft(draft, input.animation, ctx);
    const attId = ctx.resolve('attachment', input.attachment);
    const mesh = draft.attachments[attId];
    if (mesh?.type !== 'mesh') throwError('VALIDATION', undefined, { path: 'attachment', hint: 'deform keys need a mesh attachment' });
    const offsets = meshOffsets.trim(fullOffsets(mesh, input));
    const keys = anim.deform[attId];
    const same = keys?.find((k) => k.f === input.frame);
    if (unchanged(same, offsets, input.curve)) return;
    if (same) {
      same.offsets = offsets;
      if (input.curve !== undefined) same.curve = input.curve;
    } else {
      const key: DeformKey = { f: input.frame, offsets, ...(input.curve === undefined ? {} : { curve: input.curve }) };
      insertByFrame(keys, key, (next) => {
        anim.deform[attId] = next;
      });
    }
    draft.meta.updatedAt = ctx.now;
  },
});
